import {
  CaptionedImage,
  DetailsPanel,
  GenderIcon,
  LoadingIcon,
} from '@/components/controls';
import { useGroupOptions, useNichePersonImage, useUserInfo } from '@/hooks';
import { toProperCase } from '@/utils';
import {
  Cake as CakeIcon,
  Checklist as ChecklistIcon,
  Map as MapIcon,
  MarkChatRead as MarkChatReadIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import {
  alpha,
  Card,
  CardHeader,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { differenceInYears, format } from 'date-fns';
import { Fragment, useMemo } from 'react';
import { useSharedMissingPerson } from '../useSharedMissingPerson';
import { RiskAvatar } from './RiskAvatar';

export function MissingPerson({ missingPerson, onShowMapClick, onShareClick }) {
  const { subject, occurrence } = missingPerson;
  const groupLookup = useGroupOptions();
  const {
    query: { data: imageSrc, isFetching: imageFetching },
  } = useNichePersonImage(subject?.id);
  const {
    query: { data: share, isFetching: shareFetching },
  } = useSharedMissingPerson(subject?.id);
  const {
    query: {
      data: { briefingRole },
    },
  } = useUserInfo();
  const groupsEntries = useMemo(
    () =>
      Object.entries(
        (missingPerson?.groupCodes ?? [])
          .filter((code) => groupLookup[code])
          .map((code) => groupLookup[code])
          .reduce(
            (acc, group) => ({
              ...acc,
              [group.type]: [...(acc[group.type] ?? []), group.name],
            }),
            {},
          ),
      ).map(([type, names]) => ({
        label: type,
        value: names.join(', '),
      })),
    [missingPerson, groupLookup],
  );
  const isChild = subject?.dateOfBirth
    ? differenceInYears(new Date(), subject.dateOfBirth) < 18
    : false;
  const isReturned =
    missingPerson?.returnInterviewCompleted ||
    missingPerson?.supervisorReturnInterviewCompleted ||
    missingPerson?.supervisorQuestionSetCompleted;

  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        minWidth: 240,
        bgcolor: isReturned
          ? alpha(theme.palette.success.main, 0.2)
          : isChild
            ? alpha(theme.palette.secondary.main, 0.2)
            : undefined,
        borderColor: isReturned
          ? theme.palette.success.dark
          : isChild
            ? theme.palette.secondary.dark
            : undefined,
      })}
    >
      <CardHeader
        avatar={<RiskAvatar risk={missingPerson?.risk} />}
        action={
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <LoadingIcon isLoading={shareFetching || imageFetching} />
            {['ADMIN', 'SUPERVISOR'].includes(briefingRole) && (
              <Tooltip title={share?.code ? 'Edit share details' : 'Share'}>
                <IconButton
                  size="small"
                  onClick={onShareClick(share?.code ?? 'new', subject?.id)}
                >
                  <ShareIcon
                    color={share?.code ? 'primary' : 'inherit'}
                    sx={{ fontSize: 'inherit' }}
                  />
                </IconButton>
              </Tooltip>
            )}
            {subject?.addresses?.length > 0 && (
              <Tooltip title="Show map">
                <IconButton size="small" onClick={onShowMapClick}>
                  <MapIcon sx={{ fontSize: 'inherit' }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        }
        title={toProperCase(
          [subject?.forenames, subject?.surname].filter(Boolean).join(' '),
        )}
      />
      <CaptionedImage
        imageSrc={imageSrc}
        alt="Photo of missing person"
        borderRadius={0}
        topCaption={isReturned ? 'Returned' : undefined}
        bottomCaption={isChild ? 'Child' : undefined}
        width="100%"
        fallbackHeight={320}
        bottomCaptionProps={{
          sx: { color: 'warning.light', fontWeight: 'bold' },
        }}
        topCaptionProps={{ sx: { color: 'success.light', fontWeight: 'bold' } }}
      />
      <Stack spacing={1} sx={{ p: 1 }}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
          <Typography
            variant="body3"
            color="textSecondary"
            sx={{ flexGrow: 1 }}
          >
            {occurrence?.number}
          </Typography>
          <GenderIcon gender={subject?.gender} />
          {subject?.dateOfBirth && (
            <Fragment>
              <CakeIcon sx={{ fontSize: 12 }} />
              <Typography variant="subtitle2">
                {format(subject.dateOfBirth, 'dd/MM/yyyy')}
              </Typography>
              <Typography variant="subtitle2" sx={{ color: 'primary.main' }}>
                ({differenceInYears(new Date(), subject.dateOfBirth)})
              </Typography>
            </Fragment>
          )}
        </Stack>
        {share?.code && (
          <Typography variant="body2" sx={{ p: 0.5 }}>
            {share?.reason}
          </Typography>
        )}
        <DetailsPanel
          entries={[
            {
              label: 'Reported missing',
              value: occurrence?.reportedTime
                ? format(occurrence.reportedTime, 'dd/MM/yyyy HH:mm')
                : '',
            },
            {
              label: 'Last seen',
              value: missingPerson?.lastSeenTime
                ? format(missingPerson.lastSeenTime, 'dd/MM/yyyy HH:mm')
                : '',
            },
            {
              label: 'Last log entry',
              value: missingPerson?.lastLogEntryTime
                ? format(missingPerson.lastLogEntryTime, 'dd/MM/yyyy HH:mm')
                : '',
            },
            {
              label: 'Next review due',
              value: missingPerson?.nextReviewTime
                ? format(missingPerson.nextReviewTime, 'dd/MM/yyyy HH:mm')
                : '',
            },
          ]}
        />
        <DetailsPanel
          title="Owned by"
          entries={[
            {
              label: 'OIC',
              value: occurrence?.officerInCase?.collarNumber,
            },
            ...groupsEntries,
          ]}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ p: 1, justifyContent: 'flex-end' }}
        >
          <Tooltip
            title={
              missingPerson?.returnInterviewCompleted
                ? 'Interviewed by OIC'
                : 'Not interviewed by OIC'
            }
            placement="top-end"
            arrow
          >
            <QuestionAnswerIcon
              color={
                missingPerson?.returnInterviewCompleted ? 'success' : 'error'
              }
              sx={{ fontSize: 16 }}
            />
          </Tooltip>
          <Tooltip
            title={
              missingPerson?.supervisorReturnInterviewCompleted
                ? 'Interviewed by supervisor'
                : 'Not interviewed by supervisor'
            }
            placement="top-end"
            arrow
          >
            <MarkChatReadIcon
              color={
                missingPerson?.supervisorReturnInterviewCompleted
                  ? 'success'
                  : 'error'
              }
              sx={{ fontSize: 16 }}
            />
          </Tooltip>
          <Tooltip
            title={
              missingPerson?.supervisorQuestionSetCompleted
                ? `Question set completed`
                : 'Question set outstanding'
            }
            placement="top-end"
            arrow
          >
            <ChecklistIcon
              color={
                missingPerson?.supervisorQuestionSetCompleted
                  ? 'success'
                  : 'error'
              }
              sx={{ fontSize: 16 }}
            />
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
}
